import React from 'react';
import { AlertTriangle, Info, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from './dialog';
import { Button } from './button';
import { cn } from '../../lib/utils';

type ConfirmationVariant = 'warning' | 'danger' | 'info';

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  message?: React.ReactNode;
  note?: React.ReactNode;
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  variant?: ConfirmationVariant;
  maxWidthClassName?: string;
}

const iconByVariant = {
  warning: AlertTriangle,
  danger: Trash2,
  info: Info,
};

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onOpenChange,
  title,
  message,
  note,
  children,
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  variant = 'warning',
  maxWidthClassName = 'max-w-[700px]',
}) => {
  const Icon = iconByVariant[variant];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(maxWidthClassName, 'p-6')}>
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-hero-red-10">
            <Icon className="h-5 w-5 text-hero-red" />
          </div>
          <DialogTitle className="font-heading text-[18px] font-semibold leading-tight text-blueprint-blue">
            {title}
          </DialogTitle>
        </div>

        <div className="mb-6 space-y-3">
          {message ? <p className="text-[13px] leading-5 text-text-grey">{message}</p> : null}
          {children}
          {note ? (
            <div className="rounded-lg border border-hero-red-20 bg-hero-red-10 p-3">
              <p className="text-[13px] leading-5 text-readable-red">{note}</p>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            loading={isLoading}
            className="min-w-[100px] bg-hero-red text-white hover:bg-hero-red-60"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
